// TelcoTrack — vnořené řádky tabulky Plán vs skutečnost (Práce → kategorie → SAP).

import type { FinanceComparisonResult } from '../../core/models/object.model';
import type { FinanceMaterialComparisonLine } from '../../core/models/object.model';
import {
  FINANCE_WORK_TABLE_BUCKET_MAP,
  FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_IDS,
  FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_LABELS,
  FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_IDS,
  FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_LABELS,
  FINANCE_WORK_TABLE_TOP_CATEGORY_IDS,
  FINANCE_WORK_TABLE_TOP_CATEGORY_LABELS,
  resolveFinanceWorkTableCategoryFromLine,
  resolveFinanceWorkTableMontazSubcategoryFromLine,
  resolveFinanceWorkTableStavbaSubcategoryFromLine,
  type FinanceWorkTableMontazSubcategoryId,
  type FinanceWorkTableStavbaSubcategoryId,
  type FinanceWorkTableTopCategoryId,
} from '@core/shared-bridge';
import { resolveMaterialCategoryPath } from './materials-summary-table.util';

export interface FinanceComparisonTableRow {
  id: string;
  label: string;
  planned: number;
  actual: number;
  variance: number;
  plannedQuantity?: number;
  actualQuantity?: number;
  unit?: string | null;
  isTotal?: boolean;
  kind?: 'work' | 'material';
  rowType?: 'group' | 'category' | 'subcategory' | 'line' | 'total' | 'listedSum';
  depth?: number;
  sap?: string;
  expandable?: boolean;
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function sumBuckets(
  comparison: FinanceComparisonResult,
  kind: 'work' | 'material',
  bucketSuffixes: readonly string[],
): { planned: number; actual: number } {
  let planned = 0;
  let actual = 0;
  for (const bucket of comparison.buckets ?? []) {
    if (bucket.kind !== kind) continue;
    const suffix = bucket.id.replace(/^(work|material)-/, '');
    if (!bucketSuffixes.includes(suffix)) continue;
    planned += Number(bucket.planned ?? 0);
    actual += Number(bucket.actual ?? 0);
  }
  return { planned: roundMoney(planned), actual: roundMoney(actual) };
}

function categoryTotalsFromBuckets(
  comparison: FinanceComparisonResult,
  categoryId: FinanceWorkTableTopCategoryId,
): { planned: number; actual: number; variance: number } {
  const bucketIds = FINANCE_WORK_TABLE_BUCKET_MAP[categoryId];
  const { planned, actual } = sumBuckets(comparison, 'work', bucketIds);
  return { planned, actual, variance: roundMoney(planned - actual) };
}

/**
 * Nadpis kategorie / skupiny: všechny položky v dané množině vs jejich skutečnost.
 * Fallback na PPD bucket/import jen když ještě nemáme rozpad po SAP.
 */
function categoryHeadlineTotals(
  lines: readonly FinanceMaterialComparisonLine[],
  fallback: { planned: number; actual: number; variance: number },
  linesAvailable: boolean,
): { planned: number; actual: number; variance: number } {
  if (linesAvailable) return totalsFromLines(lines);
  return fallback;
}

function moneyFallback(
  planned: number,
  actual: number,
): { planned: number; actual: number; variance: number } {
  const p = roundMoney(planned);
  const a = roundMoney(actual);
  return { planned: p, actual: a, variance: roundMoney(p - a) };
}

/** Viditelný součet pod nadpisem — jen když se liší od totálu kategorie. */
export const FINANCE_LISTED_ITEMS_SUM_LABEL = 'Součet zobrazených položek';

function appendListedItemsSumRow(args: {
  rows: FinanceComparisonTableRow[];
  id: string;
  kind: 'work' | 'material';
  depth: number;
  headline: { planned: number; actual: number };
  listedLines: readonly FinanceMaterialComparisonLine[];
}): void {
  const listed = totalsFromLines(args.listedLines);
  if (
    Math.abs(listed.planned - args.headline.planned) < 0.015 &&
    Math.abs(listed.actual - args.headline.actual) < 0.015
  ) {
    return;
  }
  args.rows.push({
    id: args.id,
    label: FINANCE_LISTED_ITEMS_SUM_LABEL,
    planned: listed.planned,
    actual: listed.actual,
    variance: listed.variance,
    kind: args.kind,
    rowType: 'listedSum',
    depth: args.depth,
  });
}

function lineHasData(line: FinanceMaterialComparisonLine): boolean {
  return (
    Number(line.plannedCost ?? 0) !== 0 ||
    Number(line.actualCost ?? 0) !== 0 ||
    Number(line.plannedQuantity ?? 0) !== 0 ||
    Number(line.actualQuantity ?? 0) !== 0
  );
}

function workLinesForCategory(
  lines: readonly FinanceMaterialComparisonLine[],
  categoryId: FinanceWorkTableTopCategoryId,
): FinanceMaterialComparisonLine[] {
  return lines.filter(
    (line) => resolveFinanceWorkTableCategoryFromLine(line.name, undefined, line.sap) === categoryId,
  );
}

function workLinesForStavbaSubcategory(
  lines: readonly FinanceMaterialComparisonLine[],
  subcategoryId: FinanceWorkTableStavbaSubcategoryId,
): FinanceMaterialComparisonLine[] {
  return workLinesForCategory(lines, 'stavba').filter(
    (line) =>
      resolveFinanceWorkTableStavbaSubcategoryFromLine(line.name, line.sap, line.unit) === subcategoryId,
  );
}

function workLinesForMontazSubcategory(
  lines: readonly FinanceMaterialComparisonLine[],
  subcategoryId: FinanceWorkTableMontazSubcategoryId,
): FinanceMaterialComparisonLine[] {
  return workLinesForCategory(lines, 'montaz').filter(
    (line) => resolveFinanceWorkTableMontazSubcategoryFromLine(line.name) === subcategoryId,
  );
}

function totalsFromLines(
  lines: readonly FinanceMaterialComparisonLine[],
): { planned: number; actual: number; variance: number } {
  let planned = 0;
  let actual = 0;
  for (const line of lines) {
    planned += Number(line.plannedCost ?? 0);
    actual += Number(line.actualCost ?? 0);
  }
  return { planned: roundMoney(planned), actual: roundMoney(actual), variance: roundMoney(planned - actual) };
}

function roundQuantity(n: number): number {
  return Math.round(n * 10000) / 10000;
}

function lineQuantityFields(line: FinanceMaterialComparisonLine): Pick<
  FinanceComparisonTableRow,
  'plannedQuantity' | 'actualQuantity' | 'unit'
> {
  return {
    plannedQuantity: roundQuantity(Number(line.plannedQuantity ?? 0)),
    actualQuantity: roundQuantity(Number(line.actualQuantity ?? 0)),
    unit: line.unit ?? null,
  };
}

/** Součet množství podřádků — jen pokud mají stejnou jednotku. */
function aggregateLineQuantities(
  lines: readonly FinanceMaterialComparisonLine[],
): Pick<FinanceComparisonTableRow, 'plannedQuantity' | 'actualQuantity' | 'unit'> | null {
  if (lines.length === 0) return null;

  const units = new Set(lines.map((line) => line.unit?.trim() || null));
  if (units.size !== 1) return null;

  const unit = [...units][0] ?? null;
  let plannedQuantity = 0;
  let actualQuantity = 0;
  for (const line of lines) {
    plannedQuantity += Number(line.plannedQuantity ?? 0);
    actualQuantity += Number(line.actualQuantity ?? 0);
  }

  return {
    plannedQuantity: roundQuantity(plannedQuantity),
    actualQuantity: roundQuantity(actualQuantity),
    unit,
  };
}

function appendWorkLineRows(
  rows: FinanceComparisonTableRow[],
  categoryKey: string,
  lines: readonly FinanceMaterialComparisonLine[],
  depth: number,
): void {
  for (const line of lines) {
    rows.push({
      id: `work-line-${categoryKey}-${line.sap}`,
      label: `${line.sap} — ${line.name?.trim() || line.sap}`,
      sap: line.sap,
      planned: roundMoney(line.plannedCost),
      actual: roundMoney(line.actualCost),
      variance: roundMoney(line.costVariance),
      ...lineQuantityFields(line),
      kind: 'work',
      rowType: 'line',
      depth,
    });
  }
}

interface MaterialFinanceTreeNode {
  label: string;
  children: Map<string, MaterialFinanceTreeNode>;
  lines: FinanceMaterialComparisonLine[];
}

function getOrCreateMaterialTreeChild(
  parent: MaterialFinanceTreeNode,
  label: string,
): MaterialFinanceTreeNode {
  let child = parent.children.get(label);
  if (!child) {
    child = { label, children: new Map(), lines: [] };
    parent.children.set(label, child);
  }
  return child;
}

function insertMaterialLineIntoTree(
  root: MaterialFinanceTreeNode,
  line: FinanceMaterialComparisonLine,
): void {
  const path = resolveMaterialCategoryPath(line);
  let node = root;
  for (const segment of path) {
    node = getOrCreateMaterialTreeChild(node, segment);
  }
  node.lines.push(line);
}

function collectMaterialTreeLines(node: MaterialFinanceTreeNode): FinanceMaterialComparisonLine[] {
  const out = [...node.lines];
  for (const child of node.children.values()) {
    out.push(...collectMaterialTreeLines(child));
  }
  return out;
}

function materialLinesForBucket(
  lines: readonly FinanceMaterialComparisonLine[],
  bucketId: string,
): FinanceMaterialComparisonLine[] {
  const suffix = bucketId.replace(/^material-/, '');
  return lines.filter((line) => (line.financeBucket ?? 'material_ostatni') === suffix);
}

function appendMaterialTreeRows(args: {
  rows: FinanceComparisonTableRow[];
  node: MaterialFinanceTreeNode;
  depth: number;
  idPrefix: string;
  expandedTreeIds: ReadonlySet<string>;
}): void {
  const { rows, node, depth, idPrefix, expandedTreeIds } = args;
  const childEntries = Array.from(node.children.entries()).sort((a, b) =>
    a[0].localeCompare(b[0], 'cs'),
  );

  for (const [label, child] of childEntries) {
    const descendants = collectMaterialTreeLines(child).filter(lineHasData);
    const totals = totalsFromLines(descendants);
    if (totals.planned === 0 && totals.actual === 0 && descendants.length === 0) continue;

    const rowId = `${idPrefix}-${label}`;
    const qty = aggregateLineQuantities(descendants);
    const hasNestedCategories = child.children.size > 0;
    const hasLines = child.lines.some(lineHasData);

    rows.push({
      id: rowId,
      label,
      planned: totals.planned,
      actual: totals.actual,
      variance: totals.variance,
      ...(qty ?? {}),
      kind: 'material',
      rowType: 'subcategory',
      depth,
      expandable: hasNestedCategories || hasLines,
    });

    if (!expandedTreeIds.has(rowId)) continue;

    if (hasNestedCategories) {
      appendMaterialTreeRows({
        rows,
        node: child,
        depth: depth + 1,
        idPrefix: rowId,
        expandedTreeIds,
      });
    }

    const sortedLines = [...child.lines].filter(lineHasData).sort((a, b) => {
      const aMax = Math.max(Number(a.plannedCost) || 0, Number(a.actualCost) || 0);
      const bMax = Math.max(Number(b.plannedCost) || 0, Number(b.actualCost) || 0);
      return bMax - aMax || a.sap.localeCompare(b.sap, 'cs');
    });
    for (const line of sortedLines) {
      rows.push({
        id: `material-line-${rowId}-${line.sap}`,
        label: `${line.sap} — ${line.name?.trim() || line.sap}`,
        sap: line.sap,
        planned: roundMoney(line.plannedCost),
        actual: roundMoney(line.actualCost),
        variance: roundMoney(line.costVariance),
        ...lineQuantityFields(line),
        kind: 'material',
        rowType: 'line',
        depth: depth + 1,
      });
    }
  }
}

function appendNestedSubcategoryRows(args: {
  rows: FinanceComparisonTableRow[];
  parentCategoryId: 'stavba' | 'montaz';
  subcategoryIds: readonly string[];
  labels: Record<string, string>;
  resolveLines: (subId: string) => FinanceMaterialComparisonLine[];
  expandedSubcategories: ReadonlySet<string>;
}): void {
  const { rows, parentCategoryId, subcategoryIds, labels, resolveLines, expandedSubcategories } = args;

  for (const subId of subcategoryIds) {
    const subLines = resolveLines(subId).filter(lineHasData);
    const subTotals = totalsFromLines(subLines);
    if (subTotals.planned === 0 && subTotals.actual === 0 && subLines.length === 0) continue;

    const subQty = aggregateLineQuantities(subLines);

    rows.push({
      id: `work-sub-${parentCategoryId}-${subId}`,
      label: labels[subId] ?? subId,
      planned: subTotals.planned,
      actual: subTotals.actual,
      variance: subTotals.variance,
      ...(subQty ?? {}),
      kind: 'work',
      rowType: 'subcategory',
      depth: 2,
      expandable: subLines.length > 0,
    });

    if (expandedSubcategories.has(subId)) {
      appendWorkLineRows(rows, `${parentCategoryId}-${subId}`, subLines, 3);
    }
  }
}

export function buildFinanceComparisonTableRows(args: {
  comparison: FinanceComparisonResult;
  workGroupExpanded: boolean;
  expandedWorkCategories: ReadonlySet<FinanceWorkTableTopCategoryId>;
  expandedStavbaSubcategories: ReadonlySet<FinanceWorkTableStavbaSubcategoryId>;
  expandedMontazSubcategories: ReadonlySet<FinanceWorkTableMontazSubcategoryId>;
  materialGroupExpanded: boolean;
  expandedMaterialBucketIds: ReadonlySet<string>;
  /** Rozbalené uzly stromu katalogu pod bucketem materiálu (`material-tree-…`). */
  expandedMaterialTreeIds?: ReadonlySet<string>;
  workLines: readonly FinanceMaterialComparisonLine[];
  materialLines: readonly FinanceMaterialComparisonLine[];
}): FinanceComparisonTableRow[] {
  const {
    comparison: c,
    workGroupExpanded,
    expandedWorkCategories,
    expandedStavbaSubcategories,
    expandedMontazSubcategories,
    materialGroupExpanded,
    expandedMaterialBucketIds,
    expandedMaterialTreeIds = new Set<string>(),
    workLines,
    materialLines,
  } = args;

  const rows: FinanceComparisonTableRow[] = [];
  const workLinesAvailable = workLines.length > 0;
  const materialLinesAvailable = materialLines.length > 0;

  const workGroupTotals = categoryHeadlineTotals(
    workLines,
    moneyFallback(c.plannedWorkRevenue, c.actualWorkCost),
    workLinesAvailable,
  );
  const materialGroupTotals = categoryHeadlineTotals(
    materialLines,
    moneyFallback(c.plannedMaterialCost, c.actualMaterialCost),
    materialLinesAvailable,
  );

  if (workGroupTotals.planned > 0 || workGroupTotals.actual > 0 || workLinesAvailable) {
    rows.push({
      id: 'work-group',
      label: 'Práce',
      planned: workGroupTotals.planned,
      actual: workGroupTotals.actual,
      variance: workGroupTotals.variance,
      kind: 'work',
      rowType: 'group',
      depth: 0,
      expandable: true,
    });

    if (workGroupExpanded) {
      for (const categoryId of FINANCE_WORK_TABLE_TOP_CATEGORY_IDS) {
        const allCatLines = workLinesForCategory(workLines, categoryId);
        const catLines = allCatLines.filter(lineHasData);
        const totals = categoryHeadlineTotals(
          allCatLines,
          categoryTotalsFromBuckets(c, categoryId),
          workLinesAvailable,
        );
        if (totals.planned === 0 && totals.actual === 0 && catLines.length === 0) continue;

        const nested =
          categoryId === 'stavba' || categoryId === 'montaz'
            ? categoryId === 'stavba'
              ? FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_IDS.flatMap((subId) =>
                  workLinesForStavbaSubcategory(workLines, subId).filter(lineHasData),
                )
              : FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_IDS.flatMap((subId) =>
                  workLinesForMontazSubcategory(workLines, subId).filter(lineHasData),
                )
            : [];

        const catQty =
          categoryId === 'stavba' || categoryId === 'montaz' ? null : aggregateLineQuantities(catLines);

        rows.push({
          id: `work-cat-${categoryId}`,
          label: FINANCE_WORK_TABLE_TOP_CATEGORY_LABELS[categoryId],
          planned: totals.planned,
          actual: totals.actual,
          variance: totals.variance,
          ...(catQty ?? {}),
          kind: 'work',
          rowType: 'category',
          depth: 1,
          expandable:
            categoryId === 'stavba' || categoryId === 'montaz'
              ? nested.length > 0
              : catLines.length > 0,
        });

        if (!expandedWorkCategories.has(categoryId)) continue;

        if (categoryId === 'stavba') {
          appendNestedSubcategoryRows({
            rows,
            parentCategoryId: 'stavba',
            subcategoryIds: FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_IDS,
            labels: FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_LABELS,
            resolveLines: (subId) =>
              workLinesForStavbaSubcategory(workLines, subId as FinanceWorkTableStavbaSubcategoryId),
            expandedSubcategories: expandedStavbaSubcategories,
          });
          appendListedItemsSumRow({
            rows,
            id: `work-cat-${categoryId}-listed-sum`,
            kind: 'work',
            depth: 2,
            headline: totals,
            listedLines: nested,
          });
          continue;
        }

        if (categoryId === 'montaz') {
          appendNestedSubcategoryRows({
            rows,
            parentCategoryId: 'montaz',
            subcategoryIds: FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_IDS,
            labels: FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_LABELS,
            resolveLines: (subId) =>
              workLinesForMontazSubcategory(workLines, subId as FinanceWorkTableMontazSubcategoryId),
            expandedSubcategories: expandedMontazSubcategories,
          });
          appendListedItemsSumRow({
            rows,
            id: `work-cat-${categoryId}-listed-sum`,
            kind: 'work',
            depth: 2,
            headline: totals,
            listedLines: nested,
          });
          continue;
        }

        appendWorkLineRows(rows, categoryId, catLines, 2);
        appendListedItemsSumRow({
          rows,
          id: `work-cat-${categoryId}-listed-sum`,
          kind: 'work',
          depth: 2,
          headline: totals,
          listedLines: catLines,
        });
      }
    }
  }

  const materialBuckets = (c.buckets ?? []).filter((b) => b.kind === 'material');
  if (
    materialBuckets.length > 0 ||
    materialGroupTotals.planned > 0 ||
    materialGroupTotals.actual > 0 ||
    materialLinesAvailable
  ) {
    rows.push({
      id: 'material-group',
      label: 'Materiál',
      planned: materialGroupTotals.planned,
      actual: materialGroupTotals.actual,
      variance: materialGroupTotals.variance,
      kind: 'material',
      rowType: 'group',
      depth: 0,
      expandable: materialBuckets.length > 0,
    });

    if (materialGroupExpanded) {
      for (const bucket of materialBuckets) {
        const allBucketLines = materialLinesForBucket(materialLines, bucket.id);
        const bucketMaterialLines = allBucketLines.filter(lineHasData);
        const totals = categoryHeadlineTotals(
          allBucketLines,
          moneyFallback(bucket.planned, bucket.actual),
          materialLinesAvailable,
        );
        if (totals.planned === 0 && totals.actual === 0 && bucketMaterialLines.length === 0) {
          continue;
        }
        const bucketQty = aggregateLineQuantities(bucketMaterialLines);

        rows.push({
          id: bucket.id,
          label: bucket.label,
          planned: totals.planned,
          actual: totals.actual,
          variance: totals.variance,
          ...(bucketQty ?? {}),
          kind: 'material',
          rowType: 'category',
          depth: 1,
          expandable: bucketMaterialLines.length > 0,
        });

        if (!expandedMaterialBucketIds.has(bucket.id) || bucketMaterialLines.length === 0) {
          continue;
        }

        const root: MaterialFinanceTreeNode = { label: '', children: new Map(), lines: [] };
        for (const line of bucketMaterialLines) {
          insertMaterialLineIntoTree(root, line);
        }

        appendMaterialTreeRows({
          rows,
          node: root,
          depth: 2,
          idPrefix: `material-tree-${bucket.id}`,
          expandedTreeIds: expandedMaterialTreeIds,
        });
        appendListedItemsSumRow({
          rows,
          id: `${bucket.id}-listed-sum`,
          kind: 'material',
          depth: 2,
          headline: totals,
          listedLines: bucketMaterialLines,
        });
      }
    }
  }

  const totalPlanned = roundMoney(workGroupTotals.planned + materialGroupTotals.planned);
  const totalActual = roundMoney(workGroupTotals.actual + materialGroupTotals.actual);
  rows.push({
    id: 'total',
    label: 'Celkem',
    planned: totalPlanned,
    actual: totalActual,
    variance: roundMoney(totalPlanned - totalActual),
    isTotal: true,
    rowType: 'total',
    depth: 0,
  });

  return rows;
}
